import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { jsPDF } from 'jspdf'
import { db } from '../utils/firebase'
import { buildCanonicalAddress, addressesMatch } from '../utils/address'
import { getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore'
import {
  MapPin,
  Search,
  Plus,
  Copy,
  Check,
  X,
  Home,
  MinusCircle,
  PlusCircle,
  User,
  Clock,
  Trash2,
  RefreshCw,
  MoreVertical,
  Users,
  Shield,
  Download,
  Mail,
  Send,
  CheckCircle,
  AlertCircle,
  Upload,
} from 'lucide-react'
import './ManageNeighborsSection.css'

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
]

const emptyBulkRow = { address1: '', address2: '', city: '', state: '', zip: '' }

// Small round avatar for the Manage Households list. Renders the resident's
// profile photo if available, otherwise falls back to their first initial on
// a color derived from the name so adjacent rows read as distinct.
const AVATAR_PALETTE = [
  '#4CAF50', '#2e7d32', '#1976d2', '#0288d1', '#7b1fa2',
  '#c2185b', '#d84315', '#ef6c00', '#00796b', '#5d4037',
]

const initialFor = (name) => {
  if (!name) return '?'
  const trimmed = String(name).trim()
  if (!trimmed) return '?'
  return trimmed.charAt(0).toUpperCase()
}

const colorFor = (name) => {
  const source = String(name || '')
  let hash = 0
  for (let i = 0; i < source.length; i++) {
    hash = (hash * 31 + source.charCodeAt(i)) | 0
  }
  const index = Math.abs(hash) % AVATAR_PALETTE.length
  return AVATAR_PALETTE[index]
}

function ResidentAvatar({ name, photoURL, size = 24 }) {
  const [failed, setFailed] = useState(false)
  const showImage = Boolean(photoURL) && !failed
  const style = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.45),
  }
  if (showImage) {
    return (
      <div className="resident-avatar" style={style}>
        <img
          src={photoURL}
          alt={name || 'Resident'}
          onError={() => setFailed(true)}
        />
      </div>
    )
  }
  return (
    <div
      className="resident-avatar resident-avatar--initial"
      style={{ ...style, backgroundColor: colorFor(name) }}
      aria-label={name || 'Resident'}
    >
      {initialFor(name)}
    </div>
  )
}

// Format a Firestore Timestamp / Date / null into a friendly date string for
// the "Emailed on …" indicator. Supports a short ("Mar 14") and long
// ("Mar 14, 2026") variant via the `long` option, plus a graceful fallback
// when the value is missing or unparseable.
const formatEmailSentAt = (value, opts = {}) => {
  if (!value) return ''
  let date
  if (typeof value?.toDate === 'function') {
    // Firestore Timestamp
    try { date = value.toDate() } catch { return '' }
  } else if (value instanceof Date) {
    date = value
  } else if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    if (!isNaN(parsed.getTime())) date = parsed
  } else if (value && typeof value === 'object' && typeof value.seconds === 'number') {
    // Plain object that looks like a serialized Firestore Timestamp
    date = new Date(value.seconds * 1000)
  }
  if (!date || isNaN(date.getTime())) return ''
  const long = !!opts.long
  try {
    return date.toLocaleDateString(undefined, long
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

// Parse a full address string like "123 Main St, Springfield, IL 62704" into components
const parseAddressString = (addressStr) => {
  const parts = addressStr.split(',').map(p => p.trim())
  if (parts.length >= 3) {
    const address1 = parts[0]
    const city = parts[parts.length - 2]
    const stateZipPart = parts[parts.length - 1]
    const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s+(\d{5})$/)
    if (stateZipMatch) {
      return { address1, address2: '', city, state: stateZipMatch[1], zip: stateZipMatch[2] }
    }
    return { address1, address2: '', city, state: stateZipPart, zip: '' }
  } else if (parts.length === 2) {
    const address1 = parts[0]
    const stateZipMatch = parts[1].match(/^([A-Z]{2})\s+(\d{5})$/)
    if (stateZipMatch) {
      return { address1, address2: '', city: '', state: stateZipMatch[1], zip: stateZipMatch[2] }
    }
    return { address1, address2: '', city: parts[1], state: '', zip: '' }
  }
  return { address1: addressStr, address2: '', city: '', state: '', zip: '' }
}

function ManageNeighborsSection({ userProfile }) {
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [copiedCode, setCopiedCode] = useState(null)
  const [generatingCode, setGeneratingCode] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [showHouseholdModal, setShowHouseholdModal] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [householdMembers, setHouseholdMembers] = useState([])
  
  const [showAddModal, setShowAddModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailEntries, setEmailEntries] = useState([])
  // Free-text filter for the email-invitations modal. Matches against the
  // address string and the registration code so an admin with 100+ pending
  // households can quickly jump to a specific one.
  const [emailSearch, setEmailSearch] = useState('')
  const [sendingEmails, setSendingEmails] = useState(false)
  const [emailResults, setEmailResults] = useState(null)
  const [exportOptions, setExportOptions] = useState({ active: false, pending: false })
  const [exportEntries, setExportEntries] = useState([])
  const [addMode, setAddMode] = useState('single')
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState('')
  
  const [singleAddress, setSingleAddress] = useState({
    address1: '', address2: '', city: '', state: '', zip: ''
  })
  
  const [bulkRows, setBulkRows] = useState([{ ...emptyBulkRow }, { ...emptyBulkRow }, { ...emptyBulkRow }])
  const [applyToAll, setApplyToAll] = useState({ city: '', state: '', zip: '' })
  // File input for CSV upload. Hidden — triggered by the "Upload CSV"
  // button so we can style it consistently with the other actions
  // instead of using the browser's default ugly file picker.
  const csvFileInputRef = useRef(null)
  const [csvImportNote, setCsvImportNote] = useState('')

  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, vacant: 0, inactive: 0 })
  const [neighborhoodName, setNeighborhoodName] = useState("")
  const [dropdownAnchor, setDropdownAnchor] = useState(null) // { addrId, top, left, openUp }
  const dropdownRefs = useRef({})

  const handleDropdownClick = (addrId, buttonElement) => {
    // Capture the button's viewport position so we can render the menu in
    // a portal on document.body and avoid being clipped by any ancestor's
    // overflow: hidden (several wrappers around the table have it).
    const rect = buttonElement.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openUp = spaceBelow < 260
    setDropdownAnchor({
      addrId,
      top: openUp ? rect.top : rect.bottom,
      left: rect.right, // we right-align to this x; see style below
      openUp,
    })
  }

  // Close on scroll/resize so the floating menu never drifts away from
  // its anchor button. Also close on a document-level mousedown when the
  // click lands outside the menu — we used to use a full-screen backdrop
  // div for this, but it kept stealing clicks that were visually on the
  // menu items (z-index/stacking-context quirk), so we now check the
  // event target against the menu ref instead.
  useEffect(() => {
    if (!openMenuId) return

    const close = () => setOpenMenuId(null)

    const onMouseDown = (e) => {
      // If the mousedown target is inside the open menu, leave it alone —
      // the menu item's onClick will handle it. Otherwise, close.
      const menuEl = dropdownRefs.current[openMenuId]
      if (menuEl && menuEl.contains(e.target)) return
      // Also leave the anchor (three-dots) button alone so its own onClick
      // can toggle the menu closed — otherwise this listener would fire
      // first and the button click would then re-open it.
      if (e.target.closest && e.target.closest('.dropdown-container')) return
      setOpenMenuId(null)
    }

    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    document.addEventListener('mousedown', onMouseDown, true)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
      document.removeEventListener('mousedown', onMouseDown, true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenuId])

  useEffect(() => {
    loadAddresses()
  }, [userProfile])

  const loadAddresses = async () => {
    try {
      setLoading(true)
      // Fetch neighborhood name
      const neighborhoodDoc = await getDoc(doc(db, "neighborhoods", userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || userProfile.neighborhoodId)
      }

      // Query all data sources in parallel (matching iOS app behavior)
      const [addressSnap, usersSnap, invitationsSnap, householdInvitesSnap] = await Promise.all([
        getDocs(query(collection(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses'))),
        getDocs(query(collection(db, 'users'), where('neighborhoodId', '==', userProfile.neighborhoodId))),
        getDocs(query(collection(db, 'invitations'), where('neighborhoodId', '==', userProfile.neighborhoodId))),
        getDocs(query(collection(db, 'householdInvites'), where('neighborhoodId', '==', userProfile.neighborhoodId), where('status', '==', 'pending')))
      ])

      // 1. Build subcollection map (preserves doc IDs for write operations)
      const subcollectionMap = {}
      addressSnap.docs.forEach(d => {
        const data = d.data()
        subcollectionMap[data.address] = {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate() || null,
          codeGeneratedAt: data.codeGeneratedAt?.toDate() || null
        }
      })

      // 1a. Drift-resolver: when a downstream record (user/invitation/etc.)
      // has an `address` string that case-insensitively matches an existing
      // subcollection doc, we want to use the subcollection's exact string
      // as the canonical key. Otherwise drift like "13407 Seward st" vs
      // "13407 Seward St" produces two separate household rows. We also
      // collect heal-writes for any downstream doc whose stored string
      // doesn't already match the canonical version — applied in a single
      // batch at the end of this function.
      const subcollectionAddresses = Object.keys(subcollectionMap)
      const driftHeals = [] // [{ ref, canonicalAddress }]
      const resolveCanonical = (rawAddress, ref) => {
        if (!rawAddress) return rawAddress
        const match = subcollectionAddresses.find(existing => addressesMatch(existing, rawAddress))
        if (match && match !== rawAddress && ref) {
          // Queue a heal so the next load doesn't have to re-resolve.
          driftHeals.push({ ref, canonicalAddress: match })
        }
        return match || rawAddress
      }

      // 2. Group users by address (normalized against subcollection where possible)
      const membersByAddress = {}
      usersSnap.docs.forEach(d => {
        const data = d.data()
        const rawAddress = data.address || ''
        if (!rawAddress) return
        const address = resolveCanonical(rawAddress, d.ref)
        if (!membersByAddress[address]) membersByAddress[address] = []
        membersByAddress[address].push({ id: d.id, ...data, address })
      })

      // 3. Map invitation codes by address (normalized)
      const invitationCodeByAddress = {}
      const vacantFromInvitations = new Set()
      invitationsSnap.docs.forEach(d => {
        const data = d.data()
        const rawAddress = data.address || ''
        const code = data.code || ''
        if (!rawAddress) return
        const address = resolveCanonical(rawAddress, d.ref)
        if (code && !data.isUsed && !data.invalidated) {
          invitationCodeByAddress[address] = code
        } else {
          vacantFromInvitations.add(address)
        }
      })

      // 4. Group pending household invites by address (normalized)
      const pendingInvitesByAddress = {}
      householdInvitesSnap.docs.forEach(d => {
        const data = d.data()
        const rawAddress = data.address || ''
        if (!rawAddress) return
        const address = resolveCanonical(rawAddress, d.ref)
        if (!pendingInvitesByAddress[address]) pendingInvitesByAddress[address] = []
        pendingInvitesByAddress[address].push(data)
      })

      // 4a. Apply any drift-heals we collected during resolveCanonical.
      // This rewrites the stored `address` on user/invitation/householdInvite
      // docs that had a near-match string to the canonical version, so the
      // next load doesn't have to do this resolution at all.
      if (driftHeals.length > 0) {
        try {
          const healBatch = writeBatch(db)
          for (const heal of driftHeals) {
            healBatch.update(heal.ref, { address: heal.canonicalAddress })
          }
          await healBatch.commit()
          console.log(`Healed ${driftHeals.length} address(es) to canonical casing`)
        } catch (e) {
          // Non-fatal — the UI still renders correctly even if heal fails,
          // because resolveCanonical already substituted in-memory.
          console.error('Failed to heal drifted addresses:', e)
        }
      }

      // 5. Combine all addresses (union of all sources, matching iOS app)
      const allAddressStrings = new Set([
        ...Object.keys(subcollectionMap),
        ...Object.keys(membersByAddress),
        ...Object.keys(invitationCodeByAddress),
        ...vacantFromInvitations,
        ...Object.keys(pendingInvitesByAddress)
      ])

      const addressList = []
      for (const address of allAddressStrings) {
        const sub = subcollectionMap[address]
        const members = membersByAddress[address] || []
        const invitationCode = invitationCodeByAddress[address] || null
        const primaryMember = members[0] || null

        // Determine status dynamically (matching iOS logic)
        let status
        if (members.length > 0) {
          status = 'active'
        } else if (invitationCode) {
          status = 'pending'
        } else {
          status = 'vacant'
        }

        // Parse address components for entries not in subcollection
        const parsed = sub ? null : parseAddressString(address)

        // Tie the displayed invitationCode to the same source of truth that
        // status uses (the live invitations collection). Otherwise a stale
        // sub.invitationCode left over from a used/invalidated invitation
        // would show next to a "Vacant" badge — see status logic above.
        const displayedCode = invitationCode || null
        const codeGeneratedAt = displayedCode ? (sub?.codeGeneratedAt || null) : null

        addressList.push({
          id: sub?.id || null,
          address,
          address1: sub?.address1 || parsed?.address1 || address,
          address2: sub?.address2 || parsed?.address2 || '',
          city: sub?.city || parsed?.city || '',
          state: sub?.state || parsed?.state || '',
          zip: sub?.zip || parsed?.zip || '',
          status,
          invitationCode: displayedCode,
          codeGeneratedAt,
          // Carry the stored code so we can detect & clean up stale values
          // below without re-querying.
          _staleSubInvitationCode: !displayedCode && sub?.invitationCode ? sub.invitationCode : null,
          residentUserId: primaryMember?.id || sub?.residentUserId || null,
          residentName: primaryMember?.name || sub?.residentName || null,
          residentEmail: primaryMember?.email || sub?.residentEmail || null,
          residentPhotoURL: primaryMember?.profilePhotoURL || null,
          isAdmin: primaryMember?.isAdmin || false,
          isBoardMember: primaryMember?.isBoardMember || false,
          // Optional free-text titles paired with isBoardMember /
          // isAdmin, e.g. "President", "Treasurer", "HOA Manager", or a
          // custom label. Empty string / null both mean "no title" and
          // the badge renders as the plain role name.
          boardTitle: primaryMember?.boardTitle || '',
          adminTitle: primaryMember?.adminTitle || '',
          createdAt: sub?.createdAt || null,
          neighborhoodId: userProfile.neighborhoodId,
          invitationEmailSentTo: sub?.invitationEmailSentTo || null,
          invitationEmailSentAt: sub?.invitationEmailSentAt || null,
          _hasSubcollectionEntry: !!sub
        })
      }

      // 6. Auto-sync: create subcollection entries for addresses discovered
      //    from users/invitations/householdInvites but missing from the
      //    addresses subcollection. This keeps the web and iOS apps in sync
      //    and ensures all write operations (generate code, vacate, etc.) work.
      const missingAddresses = addressList.filter(a => !a._hasSubcollectionEntry)
      if (missingAddresses.length > 0) {
        const batch = writeBatch(db)
        for (const addr of missingAddresses) {
          const newRef = doc(collection(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses'))
          batch.set(newRef, {
            address: addr.address,
            address1: addr.address1,
            address2: addr.address2,
            city: addr.city,
            state: addr.state,
            zip: addr.zip,
            status: addr.status,
            invitationCode: addr.invitationCode || null,
            codeGeneratedAt: null,
            residentUserId: addr.residentUserId || null,
            residentName: addr.residentName || null,
            residentEmail: addr.residentEmail || null,
            createdAt: serverTimestamp(),
            neighborhoodId: userProfile.neighborhoodId
          })
          // Update local entry with the new subcollection doc ID
          addr.id = newRef.id
          addr._hasSubcollectionEntry = true
        }
        await batch.commit()
        console.log(`Synced ${missingAddresses.length} missing address(es) to subcollection`)
      }

      // 7. Cleanup: clear stale invitationCode/codeGeneratedAt on subcollection
      //    entries whose live invitation has been used or invalidated. This
      //    keeps the stored data in sync with what the UI shows so the next
      //    load is clean (and so iOS / Android, which may read sub.invitationCode
      //    directly, don't see ghost codes).
      const staleAddresses = addressList.filter(a => a._staleSubInvitationCode && a.id)
      if (staleAddresses.length > 0) {
        try {
          const cleanupBatch = writeBatch(db)
          for (const addr of staleAddresses) {
            cleanupBatch.update(
              doc(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses', addr.id),
              { invitationCode: null, codeGeneratedAt: null }
            )
          }
          await cleanupBatch.commit()
          console.log(`Cleared ${staleAddresses.length} stale invitation code(s) on vacant addresses`)
        } catch (e) {
          console.error('Failed to clean up stale invitation codes:', e)
        }
      }

      // Drop the helper flag so it doesn't leak into the rendered state.
      addressList.forEach(a => {
        delete a._staleSubInvitationCode
        delete a._hasSubcollectionEntry
      })

      addressList.sort((a, b) => {
        const statusOrder = { active: 0, pending: 1, vacant: 2 }
        const statusDiff = statusOrder[a.status] - statusOrder[b.status]
        if (statusDiff !== 0) return statusDiff
        return a.address.localeCompare(b.address)
      })

      setAddresses(addressList)

      const pendingCount = addressList.filter(a => a.status === 'pending').length
      const vacantCount = addressList.filter(a => a.status === 'vacant').length
      const newStats = {
        total: addressList.length,
        active: addressList.filter(a => a.status === 'active').length,
        pending: pendingCount,
        vacant: vacantCount,
        inactive: pendingCount + vacantCount
      }
      setStats(newStats)
    } catch (error) {
      console.error('Error loading addresses:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateInviteCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  }

  // Wrapper around the shared canonical builder — kept under the existing
  // name so the rest of this file's call sites don't have to change.
  // buildCanonicalAddress trims and collapses whitespace, uppercases the
  // state, and produces a stable string regardless of how the admin typed
  // the input. That eliminates the casing/whitespace drift bugs that
  // previously produced duplicate household rows.
  const formatFullAddress = (addr) => buildCanonicalAddress({
    address1: addr.address1,
    address2: addr.address2,
    city: addr.city,
    state: addr.state,
    zip: addr.zip,
  })

  const validateZip = (zip) => /^\d{5}$/.test(zip)

  const handleApplyToAll = (field, value) => {
    setApplyToAll(prev => ({ ...prev, [field]: value }))
    setBulkRows(prev => prev.map(row => ({ ...row, [field]: value })))
  }

  const updateBulkRow = (index, field, value) => {
    setBulkRows(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const addBulkRow = () => {
    setBulkRows(prev => [...prev, { 
      ...emptyBulkRow, 
      city: applyToAll.city, 
      state: applyToAll.state, 
      zip: applyToAll.zip 
    }])
  }

  const removeBulkRow = (index) => {
    if (bulkRows.length > 1) {
      setBulkRows(prev => prev.filter((_, i) => i !== index))
    }
  }

  // Downloads a CSV template with the exact column names the parser
  // expects. Included sample rows make the format self-explanatory —
  // admins editing this in Excel/Numbers see what "good" looks like
  // without having to consult docs.
  const downloadCsvTemplate = () => {
    const rows = [
      ['address1', 'address2', 'city', 'state', 'zip'],
      ['123 Main St', 'Apt 1', 'Omaha', 'NE', '68106'],
      ['125 Main St', '', 'Omaha', 'NE', '68106'],
      ['127 Main St', 'Unit B', 'Omaha', 'NE', '68106'],
    ]
    // Manual CSV serialization (rather than pulling in papaparse just
    // for this) — quote any cell containing a comma, quote, or newline,
    // and escape embedded quotes by doubling them. Standard RFC 4180.
    const csv = rows.map(row => row.map(cell => {
      const s = String(cell ?? '')
      if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    }).join(',')).join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'neighbortopia-households-template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Minimal CSV parser — handles quoted fields, escaped quotes, and
  // Windows/Mac line endings. Written inline instead of adding a
  // papaparse dependency for what amounts to five columns of household
  // data. Returns an array of rows, each an array of string cells.
  const parseCsv = (text) => {
    const rows = []
    let row = []
    let cell = ''
    let inQuotes = false
    let i = 0
    // Strip a BOM (Excel exports one on UTF-8 CSVs) so it doesn't leak
    // into the first cell's value.
    if (text.charCodeAt(0) === 0xFEFF) i = 1
    while (i < text.length) {
      const ch = text[i]
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { cell += '"'; i += 2; continue }
          inQuotes = false; i++; continue
        }
        cell += ch; i++; continue
      }
      if (ch === '"') { inQuotes = true; i++; continue }
      if (ch === ',') { row.push(cell); cell = ''; i++; continue }
      if (ch === '\n' || ch === '\r') {
        row.push(cell); rows.push(row); row = []; cell = ''
        // Skip the \n in \r\n so we don't record an empty row.
        if (ch === '\r' && text[i + 1] === '\n') i += 2; else i++
        continue
      }
      cell += ch; i++
    }
    // Flush the last field / row if the file doesn't end with a newline.
    if (cell.length > 0 || row.length > 0) { row.push(cell); rows.push(row) }
    // Drop trailing entirely-empty rows so a trailing newline doesn't
    // generate a phantom row.
    while (rows.length > 0 && rows[rows.length - 1].every(c => c === '')) rows.pop()
    return rows
  }

  // Handles the <input type="file"> change event. Reads the CSV, maps
  // columns by header name (case-insensitive, order-independent), and
  // replaces `bulkRows` with the imported data. Header aliases mean
  // admins can hand us "Address 1" / "ZIP Code" / "Zip" / etc. and the
  // import still works.
  const handleCsvFileSelected = async (event) => {
    setCsvImportNote('')
    setAddError('')
    const file = event.target.files?.[0]
    // Reset the input so re-selecting the same file re-fires onChange.
    // Without this, picking the same CSV twice in a row does nothing.
    if (event.target) event.target.value = ''
    if (!file) return

    try {
      const text = await file.text()
      const rows = parseCsv(text)
      if (rows.length === 0) {
        setAddError('The CSV file appears to be empty.')
        return
      }

      // Header row → column-name → index map. Aliases keep things
      // forgiving for admins who tweak column names in Excel.
      const rawHeaders = rows[0].map(h => (h || '').trim().toLowerCase())
      const headerAliases = {
        address1: ['address1', 'address 1', 'address_1', 'street', 'street address', 'street_address'],
        address2: ['address2', 'address 2', 'address_2', 'unit', 'apt', 'apartment', 'suite'],
        city: ['city', 'town'],
        state: ['state', 'st', 'state code'],
        zip: ['zip', 'zip code', 'zipcode', 'postal', 'postal code', 'postalcode'],
      }
      const columnIndex = {}
      for (const [key, aliases] of Object.entries(headerAliases)) {
        const idx = rawHeaders.findIndex(h => aliases.includes(h))
        if (idx !== -1) columnIndex[key] = idx
      }
      if (columnIndex.address1 === undefined) {
        setAddError('CSV is missing an "address1" column. Download the template above to see the expected format.')
        return
      }

      const parsedRows = []
      const skipped = []
      for (let r = 1; r < rows.length; r++) {
        const cells = rows[r]
        const pick = (key) => {
          const idx = columnIndex[key]
          if (idx === undefined) return ''
          return (cells[idx] || '').trim()
        }
        const address1 = pick('address1')
        if (!address1) continue // Skip empty rows silently
        // Coerce state to uppercase, ZIP to digits only. Doesn't reject
        // rows that fail — the existing per-row form will highlight
        // anything missing when the admin submits.
        const state = pick('state').toUpperCase().slice(0, 2)
        const zip = pick('zip').replace(/\D/g, '').slice(0, 5)
        parsedRows.push({
          address1,
          address2: pick('address2'),
          city: pick('city'),
          state,
          zip,
        })
        // Flag rows where required data looks off so the admin knows to
        // review them in the table below — we don't block the import,
        // since the existing per-row validation on submit is the source
        // of truth for what actually gets saved.
        if (!pick('city') || !state || zip.length !== 5) {
          skipped.push(r + 1) // 1-indexed row number, +1 for header
        }
      }

      if (parsedRows.length === 0) {
        setAddError('No valid rows found in the CSV. Make sure the address1 column is filled in.')
        return
      }

      setBulkRows(parsedRows)
      const noteParts = [`Loaded ${parsedRows.length} row${parsedRows.length === 1 ? '' : 's'} from ${file.name}.`]
      if (skipped.length > 0) {
        const preview = skipped.slice(0, 5).join(', ')
        noteParts.push(`Review row${skipped.length === 1 ? '' : 's'} ${preview}${skipped.length > 5 ? '…' : ''} — city, state, or ZIP looks incomplete.`)
      }
      setCsvImportNote(noteParts.join(' '))
    } catch (err) {
      console.error('CSV import failed', err)
      setAddError(`Couldn't read the CSV file: ${err.message || 'unknown error'}`)
    }
  }

  const handleAddAddresses = async () => {
    setAddError('')
    
    let addressesToAdd = []
    
    if (addMode === 'single') {
      if (!singleAddress.address1.trim()) {
        setAddError('Please enter a street address')
        return
      }
      if (!singleAddress.city.trim()) {
        setAddError('Please enter a city')
        return
      }
      if (!singleAddress.state) {
        setAddError('Please select a state')
        return
      }
      if (!validateZip(singleAddress.zip)) {
        setAddError('Please enter a valid 5-digit ZIP code')
        return
      }
      addressesToAdd = [singleAddress]
    } else {
      const filledRows = bulkRows.filter(row => row.address1.trim())
      if (filledRows.length === 0) {
        setAddError('Please enter at least one address')
        return
      }
      
      for (let i = 0; i < filledRows.length; i++) {
        const row = filledRows[i]
        if (!row.city.trim()) {
          setAddError(`Row ${i + 1}: Please enter a city`)
          return
        }
        if (!row.state) {
          setAddError(`Row ${i + 1}: Please select a state`)
          return
        }
        if (!validateZip(row.zip)) {
          setAddError(`Row ${i + 1}: Please enter a valid 5-digit ZIP code`)
          return
        }
      }
      addressesToAdd = filledRows
    }

    try {
      setAdding(true)

      // Check for duplicates first. We pull every address doc in this
      // neighborhood once and compare case-insensitively via addressesMatch
      // — a plain Firestore `==` query would miss near-duplicates like
      // "13407 Seward St" vs "13407 seward st" and let the admin re-add a
      // house that already exists in a different casing.
      const allExistingSnap = await getDocs(
        collection(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses')
      )
      const existingAddresses = allExistingSnap.docs.map(d => d.data().address || '')

      const duplicates = []
      for (const addr of addressesToAdd) {
        const fullAddress = formatFullAddress(addr)
        if (existingAddresses.some(existing => addressesMatch(existing, fullAddress))) {
          duplicates.push(addr.address1)
        }
      }

      if (duplicates.length > 0) {
        if (duplicates.length === 1) {
          setAddError(`This address already exists in your neighborhood: ${duplicates[0]}`)
        } else {
          setAddError(`These addresses already exist in your neighborhood: ${duplicates.join(', ')}`)
        }
        setAdding(false)
        return
      }

      const batch = writeBatch(db)

      for (const addr of addressesToAdd) {
        const fullAddress = formatFullAddress(addr)
        const addressRef = doc(collection(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses'))
        batch.set(addressRef, {
          address: fullAddress,
          address1: addr.address1.trim(),
          address2: addr.address2?.trim() || '',
          city: addr.city.trim(),
          state: addr.state,
          zip: addr.zip,
          status: 'vacant',
          invitationCode: null,
          codeGeneratedAt: null,
          residentUserId: null,
          residentName: null,
          residentEmail: null,
          createdAt: serverTimestamp(),
          neighborhoodId: userProfile.neighborhoodId
        })
      }

      await batch.commit()
      
      setSingleAddress({ address1: '', address2: '', city: '', state: '', zip: '' })
      setBulkRows([{ ...emptyBulkRow }, { ...emptyBulkRow }, { ...emptyBulkRow }])
      setApplyToAll({ city: '', state: '', zip: '' })
      setShowAddModal(false)
      loadAddresses()
    } catch (error) {
      console.error('Error adding addresses:', error)
      setAddError('Failed to add addresses. Please try again.')
    } finally {
      setAdding(false)
    }
  }

  const handleGenerateCode = async (addressId, addressString) => {
    try {
      setGeneratingCode(addressId)
      const code = generateInviteCode()

      await updateDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses', addressId), {
        invitationCode: code,
        codeGeneratedAt: serverTimestamp(),
        status: 'pending'
      })

      await addDoc(collection(db, 'invitations'), {
        code,
        addressId,
        address: addressString,
        neighborhoodId: userProfile.neighborhoodId,
        neighborhoodName: userProfile.neighborhoodName || '',
        isUsed: false,
        createdAt: serverTimestamp(),
        createdBy: userProfile.uid
      })

      // Update local state instead of reloading
      setAddresses(prev => prev.map(addr =>
        addr.id === addressId
          ? { ...addr, invitationCode: code, codeGeneratedAt: new Date(), status: 'pending' }
          : addr
      ))

      // Update stats (inactive stays same since it's pending + vacant)
      setStats(prev => ({
        ...prev,
        vacant: prev.vacant - 1,
        pending: prev.pending + 1
      }))
    } catch (error) {
      console.error('Error generating code:', error)
      alert('Failed to generate code. Please try again.')
    } finally {
      setGeneratingCode(null)
    }
  }

  const handleRegenerateCode = async (addressId, oldCode, addressString) => {
    if (!window.confirm('This will invalidate the old code. Continue?')) return
    
    try {
      setGeneratingCode(addressId)
      const newCode = generateInviteCode()
      
      await updateDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses', addressId), {
        invitationCode: newCode,
        codeGeneratedAt: serverTimestamp()
      })
      
      const oldInviteQuery = query(
        collection(db, 'invitations'),
        where('code', '==', oldCode)
      )
      const oldInviteSnap = await getDocs(oldInviteQuery)
      for (const inviteDoc of oldInviteSnap.docs) {
        await updateDoc(inviteDoc.ref, { isUsed: true, invalidated: true })
      }
      
      await addDoc(collection(db, 'invitations'), {
        code: newCode,
        addressId,
        address: addressString,
        neighborhoodId: userProfile.neighborhoodId,
        neighborhoodName: userProfile.neighborhoodName || '',
        isUsed: false,
        createdAt: serverTimestamp(),
        createdBy: userProfile.uid
      })
      
      loadAddresses()
    } catch (error) {
      console.error('Error regenerating code:', error)
      alert('Failed to regenerate code. Please try again.')
    } finally {
      setGeneratingCode(null)
    }
  }

  const handleDeleteAddress = async (addressId, address) => {
    if (!window.confirm(`Delete "${address}"? This cannot be undone.`)) return

    try {
      const deletePromises = []

      // 1. Delete from addresses subcollection
      if (addressId) {
        deletePromises.push(deleteDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses', addressId)))
      }

      // 2. Delete matching invitation docs (so the address doesn't reappear)
      const invSnap = await getDocs(query(
        collection(db, 'invitations'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        where('address', '==', address)
      ))
      invSnap.docs.forEach(d => deletePromises.push(deleteDoc(d.ref)))

      // 3. Delete matching householdInvites docs
      const hiSnap = await getDocs(query(
        collection(db, 'householdInvites'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        where('address', '==', address)
      ))
      hiSnap.docs.forEach(d => deletePromises.push(deleteDoc(d.ref)))

      // 4. Detach any users that still point at this address from this
      //    neighborhood. Without this step the next loadAddresses() call
      //    would auto-recreate the address subcollection doc from the
      //    user record and the household would appear to undelete itself.
      const usersSnap = await getDocs(query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        where('address', '==', address)
      ))
      usersSnap.docs.forEach(u => {
        // Safety net: never strip the currently signed-in admin from their
        // own neighborhood (the rules would reject it and we'd brick our
        // own session). The dropdown already hides Delete on self-rows,
        // but this is here for any future code paths.
        if (u.id === userProfile.uid) return
        deletePromises.push(updateDoc(u.ref, {
          address: '',
          neighborhoodId: '',
          isAdmin: false,
          isBoardMember: false,
        }))
      })

      await Promise.all(deletePromises)
      loadAddresses()
    } catch (error) {
      console.error('Error deleting address:', error)
      const detail = error?.message || error?.code || 'unknown error'
      alert(`Failed to delete address.\n\n${detail}`)
    }
  }

  const handleVacateAddress = async (addressId, address) => {
    if (!window.confirm(`Are you sure you want to vacate "${address}"? This will remove the resident and reset the invitation code.`)) return

    try {
      if (!addressId) {
        // No subcollection entry exists — find or create one
        const addressSnap = await getDocs(query(
          collection(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses'),
          where('address', '==', address)
        ))
        if (!addressSnap.empty) {
          addressId = addressSnap.docs[0].id
        } else {
          // Address only exists in users/invitations — just reload
          alert('Could not find this address record. Please try again.')
          loadAddresses()
          return
        }
      }

      // Also remove the invitation code from the invitations collection
      const invitationsSnap = await getDocs(query(
        collection(db, 'invitations'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        where('address', '==', address)
      ))
      const batch = writeBatch(db)
      invitationsSnap.docs.forEach(invDoc => {
        batch.update(invDoc.ref, { invalidated: true })
      })

      batch.update(doc(db, 'neighborhoods', userProfile.neighborhoodId, 'addresses', addressId), {
        status: 'vacant',
        invitationCode: null,
        codeGeneratedAt: null,
        residentUserId: null,
        residentName: null,
        residentEmail: null
      })

      // Detach any users currently living at this address from the
      // neighborhood. Without this, the loader would re-derive the
      // resident on next refresh from the user doc and the row would
      // flip back to Active, defeating the vacate.
      const usersSnap = await getDocs(query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        where('address', '==', address)
      ))
      usersSnap.docs.forEach(u => {
        // Never strip the current admin from their own neighborhood —
        // they'd lose access immediately. The UI hides Vacate on the
        // signed-in user's own row, so this is defensive only.
        if (u.id === userProfile.uid) return
        batch.update(u.ref, {
          address: '',
          neighborhoodId: '',
          isAdmin: false,
          isBoardMember: false,
        })
      })

      await batch.commit()
      // Update local state without full reload to preserve scroll position
      setAddresses(prev => prev.map(a =>
        a.address === address
          ? { ...a, status: 'vacant', invitationCode: null, codeGeneratedAt: null, residentUserId: null, residentName: null, residentEmail: null, isAdmin: false, isBoardMember: false }
          : a
      ))
    } catch (error) {
      console.error('Error vacating address:', error)
      const detail = error?.message || error?.code || 'unknown error'
      alert(`Failed to vacate address.\n\n${detail}`)
    }
  }
  const handleViewHousehold = async (addr) => {
    setSelectedAddress(addr)
    try {
      // Fetch active members and pending household invites in parallel
      const [usersSnap, pendingSnap] = await Promise.all([
        getDocs(query(
          collection(db, "users"),
          where("address", "==", addr.address)
        )),
        getDocs(query(
          collection(db, "householdInvites"),
          where("neighborhoodId", "==", userProfile.neighborhoodId),
          where("address", "==", addr.address),
          where("status", "==", "pending")
        ))
      ])
      const activeMembers = usersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        memberType: 'active'
      }))
      const pendingMembers = pendingSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        memberType: 'pending'
      }))
      setHouseholdMembers([...activeMembers, ...pendingMembers])
      setShowHouseholdModal(true)
    } catch (error) {
      console.error("Error loading household:", error)
      alert("Failed to load household members.")
    }
  }

  // Is this address row the currently-signed-in admin's own household?
  // Used to gate self-demotion options — an admin demoting themselves to a
  // plain resident would instantly lose access to this page, so we hide the
  // Remove options and only expose Switch (which keeps an admin role).
  const isSelfRow = (addr) => {
    if (!addr || !userProfile) return false
    if (addr.residentUserId && userProfile.uid) {
      return addr.residentUserId === userProfile.uid
    }
    if (addr.residentEmail && userProfile.email) {
      return addr.residentEmail.toLowerCase() === userProfile.email.toLowerCase()
    }
    return false
  }

  // Prompt an admin for the title paired with a Board Member or
  // Neighborhood Admin assignment. Uses window.prompt to stay consistent
  // with the other confirm/prompt affordances in this file — a full
  // modal would be heavier than this UX needs.
  //
  // Returns:
  //   - string: the chosen title (may be '' to explicitly clear it)
  //   - undefined: the user cancelled — caller should abort
  const promptForRoleTitle = (current) => {
    const message = 'Add a custom title (e.g. President, Secretary, etc.)'
    // `window.prompt` returns null on Cancel and '' on OK-with-empty.
    // We map null → undefined so callers can distinguish "cancelled" from
    // "explicitly cleared".
    const result = window.prompt(message, current || '')
    if (result === null) return undefined
    return result.trim()
  }

  const handleSetRole = async (addr, action) => {
    // action: 'makeAdmin', 'makeBoardMember', 'removeAdmin', 'removeBoardMember', 'switchToAdmin', 'switchToBoardMember', 'editBoardTitle'
    if (!addr.residentEmail && !addr.residentUserId) {
      alert("No resident found for this address")
      return
    }

    // Block self-demotion server-side too, in case the dropdown is ever
    // shown for the current user. Switching admin↔board is fine because
    // both keep admin access.
    if (isSelfRow(addr) && (action === 'removeAdmin' || action === 'removeBoardMember')) {
      alert(
        "You can't remove yourself as an admin — you'd lose access to this page. " +
        "Ask another admin to remove you, or switch to the other admin role instead."
      )
      return
    }

    const actionMessages = {
      makeAdmin: `Make ${addr.residentName} a Neighborhood Admin?`,
      makeBoardMember: `Make ${addr.residentName} a Board Member?`,
      removeAdmin: `Remove ${addr.residentName} as Neighborhood Admin?`,
      removeBoardMember: `Remove ${addr.residentName} as Board Member?`,
      switchToAdmin: `Switch ${addr.residentName} from Board Member to Neighborhood Admin?`,
      switchToBoardMember: `Switch ${addr.residentName} from Neighborhood Admin to Board Member?`,
      editBoardTitle: `Edit ${addr.residentName}'s board title.`,
      editAdminTitle: `Edit ${addr.residentName}'s admin title.`
    }

    // The edit-title actions skip the yes/no confirm — the title prompt
    // (with Cancel) is confirmation enough.
    const isEditTitleAction = action === 'editBoardTitle' || action === 'editAdminTitle'
    if (!isEditTitleAction && !window.confirm(actionMessages[action])) return

    // For role assignments and title edits, collect a title. Empty /
    // cancel = no title (or, for cancel, abort entirely).
    let boardTitle = null
    let adminTitle = null
    if (action === 'makeBoardMember' || action === 'switchToBoardMember' || action === 'editBoardTitle') {
      boardTitle = promptForRoleTitle(addr.boardTitle || '')
      if (boardTitle === undefined) return
    }
    if (action === 'makeAdmin' || action === 'switchToAdmin' || action === 'editAdminTitle') {
      adminTitle = promptForRoleTitle(addr.adminTitle || '')
      if (adminTitle === undefined) return
    }

    try {
      // Prefer the live user doc ID we already loaded — it's faster and
      // sidesteps stale email mismatches (e.g. resident changed their email
      // but addr.residentEmail wasn't refreshed).
      let userDocId = addr.residentUserId || null

      if (!userDocId) {
        const usersQuery = query(
          collection(db, "users"),
          where("email", "==", addr.residentEmail)
        )
        const usersSnap = await getDocs(usersQuery)
        if (usersSnap.empty) {
          alert("User not found")
          return
        }
        userDocId = usersSnap.docs[0].id
      }

      let updates = {}
      switch (action) {
        case 'makeAdmin':
          // Clear any board title carried over from a prior board role
          // so the badges don't leak the wrong title.
          updates = {
            isAdmin: true,
            isBoardMember: false,
            adminTitle: adminTitle || '',
            boardTitle: '',
          }
          break
        case 'makeBoardMember':
          updates = {
            isBoardMember: true,
            isAdmin: false,
            boardTitle: boardTitle || '',
            adminTitle: '',
          }
          break
        case 'removeAdmin':
          // Clear the title too — it's meaningless without the role.
          updates = { isAdmin: false, adminTitle: '' }
          break
        case 'removeBoardMember':
          updates = { isBoardMember: false, boardTitle: '' }
          break
        case 'switchToAdmin':
          updates = {
            isAdmin: true,
            isBoardMember: false,
            adminTitle: adminTitle || '',
            boardTitle: '',
          }
          break
        case 'switchToBoardMember':
          updates = {
            isBoardMember: true,
            isAdmin: false,
            boardTitle: boardTitle || '',
            adminTitle: '',
          }
          break
        case 'editBoardTitle':
          // Title-only update; leaves role flags alone.
          updates = { boardTitle: boardTitle || '' }
          break
        case 'editAdminTitle':
          updates = { adminTitle: adminTitle || '' }
          break
      }

      await updateDoc(doc(db, "users", userDocId), updates)
      loadAddresses()
    } catch (error) {
      console.error("Error updating role:", error)
      const detail = error?.message || error?.code || "unknown error"
      alert(`Failed to update user role.\n\n${detail}`)
    }
  }

  const handleToggleRole = async (userId, field, currentValue, member) => {
    const roleNames = { isAdmin: "Neighborhood Admin", isBoardMember: "Board Member" }
    const otherField = field === "isAdmin" ? "isBoardMember" : "isAdmin"
    const hasOtherRole = member && member[otherField]

    let confirmMsg
    if (currentValue) {
      confirmMsg = `Are you sure you want to remove this user as ${roleNames[field]}?`
    } else if (hasOtherRole) {
      confirmMsg = `This will switch this user from ${roleNames[otherField]} to ${roleNames[field]}. Continue?`
    } else {
      confirmMsg = `Are you sure you want to make this user a ${roleNames[field]}?`
    }
    if (!window.confirm(confirmMsg)) return

    try {
      const updates = { [field]: !currentValue }
      // If granting a role, remove the other role (users can only be one)
      if (!currentValue) {
        updates[otherField] = false
      }
      await updateDoc(doc(db, "users", userId), updates)
      handleViewHousehold(selectedAddress)
    } catch (error) {
      console.error("Error updating role:", error)
      alert("Failed to update user role.")
    }
  }

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }


  // Helper to format address for display
  const formatAddressDisplay = (addr) => {
    // If we have structured fields, use them
    if (addr.address1 && addr.city) {
      return {
        line1: addr.address1,
        line2: addr.address2 || null,
        line3: `${addr.city}, ${addr.state} ${addr.zip}`
      }
    }
    // Fallback: just show the combined address
    return { line1: addr.address || '', line2: null, line3: null }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="status-badge active"><Check size={12} /> Active</span>
      case 'pending':
        return <span className="status-badge pending"><Clock size={12} /> Pending</span>
      default:
        return <span className="status-badge vacant"><Home size={12} /> Vacant</span>
    }
  }

  const filteredAddresses = addresses.filter(addr => {
    const matchesSearch = addr.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (addr.residentName && addr.residentName.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesStatus = statusFilter === 'all' || addr.status === statusFilter ||
                         (statusFilter === 'inactive' && (addr.status === 'pending' || addr.status === 'vacant'))
    return matchesSearch && matchesStatus
  })

  // Parse a stored address string into a USPS-style two-line label:
  //   line 1 - street (and unit/apt if present)
  //   line 2 - city, state zip
  // Falls back to addr.city / addr.state / addr.zip if the address string
  // doesn't carry that detail.
  const splitMailingAddress = (addr) => {
    const parts = (addr.address || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)

    if (parts.length >= 4) {
      // e.g., "1222 w street, Omaha, NE, 68154"
      // or    "1223 test, Unit 31, Omaha, NE, 68173"
      const street = parts.slice(0, -3).join(", ")
      const cityStateZipLine = `${parts[parts.length - 3]}, ${parts[parts.length - 2]} ${parts[parts.length - 1]}`
      return { streetLine: street, cityStateZipLine }
    }
    if (parts.length === 3) {
      // e.g., "1222 w street, Omaha, NE 68154"
      return { streetLine: parts[0], cityStateZipLine: `${parts[1]}, ${parts[2]}` }
    }
    if (parts.length === 2) {
      return { streetLine: parts[0], cityStateZipLine: parts[1] }
    }

    // Fallback: street from whatever's in addr.address, city/state/zip
    // from the separately stored fields.
    const cityState = [addr.city, addr.state].filter(Boolean).join(", ")
    const cityStateZipLine = (cityState + (addr.zip ? ` ${addr.zip}` : "")).trim()
    return { streetLine: parts[0] || "", cityStateZipLine }
  }

  // Draws a single 4x6 postcard front — NeighborTopia branding,
  // benefits, and step-by-step signup instructions (with the recipient's
  // invitation code embedded so they can register without hunting for
  // it). Called once per selected address inside handleExportLabels
  // before the Avery labels sheet.
  const drawPostcardFront = (doc, logoDataUrl) => {
    const W = 6
    const H = 4
    const margin = 0.3

    // Brand bar background
    doc.setFillColor(124, 182, 95) // --color-primary #7CB65F
    doc.rect(0, 0, W, 0.9, 'F')

    // Logo (if loaded) — the circle-masked PNG sits inside a thin
    // white outer ring that shows through the PNG's transparent
    // corners as the outline.
    let textX = margin
    if (logoDataUrl) {
      try {
        const logoSize = 0.65
        const ringR = logoSize / 2 + 0.03 // thin ~3% outline
        const cx = margin + logoSize / 2
        const cy = 0.45
        doc.setFillColor(255, 255, 255)
        doc.circle(cx, cy, ringR, 'F')
        doc.addImage(logoDataUrl, 'PNG', cx - logoSize / 2, cy - logoSize / 2, logoSize, logoSize)
        textX = margin + logoSize + 0.15
      } catch (_) {
        // Logo failed to embed — fall back to text-only header.
      }
    }

    // Brand mark
    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(22)
    doc.text('NeighborTopia', textX, 0.55)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text('Your neighborhood, connected.', textX, 0.78)

    // Body
    doc.setTextColor(20, 20, 20)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.text(`You're invited to join ${neighborhoodName || 'your neighborhood'} on NeighborTopia`, margin, 1.3)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    const benefits = [
      'Neighbor discussions & polls',
      'Community announcements & events',
      'Resident directory',
      'HOA payments, board & resources',
    ]
    let by = 1.55
    benefits.forEach((b) => {
      doc.text('•  ' + b, margin, by)
      by += 0.18
    })

    // Steps section
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text('How to register:', margin, by + 0.15)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    const steps = [
      '1.  Go to NeighborTopia.app',
      '2.  Choose "Register" and enter your invitation code',
      '3.  Create your account with your email & password',
    ]
    let sy = by + 0.4
    steps.forEach((s) => {
      doc.text(s, margin, sy)
      sy += 0.18
    })

    // Footer strip
    doc.setDrawColor(220, 220, 220)
    doc.setLineWidth(0.01)
    doc.line(margin, H - 0.6, W - margin, H - 0.6)

    // Left footer: NeighborTopia.app + generic support email stacked.
    doc.setTextColor(120, 120, 120)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text('NeighborTopia.app', margin, H - 0.37)
    doc.text('Questions? info@neighbortopia.app', margin, H - 0.2)

    // Right footer: this neighborhood's board/admin contact so
    // residents know a real neighbor to reach out to.
    const contactName = (userProfile?.name || '').trim()
    const contactEmail = (userProfile?.email || '').trim()
    if (contactName || contactEmail) {
      doc.setTextColor(60, 60, 60)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      if (contactName) {
        // Render as "Neighborhood Admin: {Name}" — label in regular
        // weight, name in bold — kept together on one right-aligned
        // line so residents can see who Phil is.
        doc.setFontSize(8)
        doc.setFont('helvetica', 'bold')
        const nameWidth = doc.getTextWidth(contactName)
        doc.setTextColor(60, 60, 60)
        doc.text(contactName, W - margin, H - 0.37, { align: 'right' })
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(120, 120, 120)
        const adminLabel = `${neighborhoodName || 'Neighborhood'} Neighborhood Admin: `
        doc.text(adminLabel, W - margin - nameWidth, H - 0.37, { align: 'right' })
      }
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(120, 120, 120)
      if (contactEmail) {
        doc.text(contactEmail, W - margin, H - 0.2, { align: 'right' })
      }
    }
  }

  // Draws the postcard back — return-address block top-left, stamp box
  // top-right, and a large "place mailing label here" target in the
  // lower-center that's sized to fit an Avery 5160 label (2.625" x 1").
  const drawPostcardBack = (doc) => {
    const W = 6
    const H = 4
    const margin = 0.3

    // Return address
    doc.setTextColor(20, 20, 20)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.text(neighborhoodName || 'Your Neighborhood', margin, margin + 0.15)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(90, 90, 90)
    doc.text('via NeighborTopia', margin, margin + 0.32)

    // Stamp box (top-right)
    const stampW = 1.0
    const stampH = 1.2
    const stampX = W - margin - stampW
    const stampY = margin
    doc.setDrawColor(160, 160, 160)
    doc.setLineWidth(0.015)
    doc.setLineDashPattern([0.06, 0.06], 0)
    doc.rect(stampX, stampY, stampW, stampH, 'S')
    doc.setLineDashPattern([], 0)
    doc.setTextColor(140, 140, 140)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text('PLACE', stampX + stampW / 2, stampY + stampH / 2 - 0.12, { align: 'center' })
    doc.text('STAMP', stampX + stampW / 2, stampY + stampH / 2 + 0.02, { align: 'center' })
    doc.text('HERE', stampX + stampW / 2, stampY + stampH / 2 + 0.16, { align: 'center' })

    // Address label placement target
    const labelW = 2.75
    const labelH = 1.1
    const labelX = (W - labelW) / 2
    const labelY = H / 2 - labelH / 2 + 0.25

    doc.setTextColor(120, 120, 120)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text('DELIVER TO', W / 2, labelY - 0.15, { align: 'center' })

    doc.setDrawColor(160, 160, 160)
    doc.setLineWidth(0.015)
    doc.setLineDashPattern([0.06, 0.06], 0)
    doc.rect(labelX, labelY, labelW, labelH, 'S')
    doc.setLineDashPattern([], 0)

    doc.setTextColor(150, 150, 150)
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(8)
    doc.text('Place mailing label here', W / 2, labelY + labelH / 2 + 0.04, { align: 'center' })

    // Footer
    doc.setTextColor(120, 120, 120)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.text('NeighborTopia.app', W / 2, H - 0.2, { align: 'center' })
  }

  // Fetch /logo.jpeg, mask it into a circle on a canvas, and return a
  // PNG data URL with transparent corners. jsPDF then embeds this so
  // the postcard can draw a thin white ring behind the logo (the ring
  // shows through the transparent corners as the outline). Returns
  // null on any failure so the postcard falls back to a text header.
  const loadLogoDataUrl = async () => {
    try {
      const res = await fetch('/logo.jpeg')
      if (!res.ok) return null
      const blob = await res.blob()
      const imgUrl = URL.createObjectURL(blob)
      const img = await new Promise((resolve, reject) => {
        const i = new Image()
        i.onload = () => resolve(i)
        i.onerror = reject
        i.src = imgUrl
      })
      const size = Math.min(img.width, img.height)
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
      ctx.closePath()
      ctx.clip()
      // Center-crop the source into the square canvas.
      const sx = (img.width - size) / 2
      const sy = (img.height - size) / 2
      ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size)
      URL.revokeObjectURL(imgUrl)
      return canvas.toDataURL('image/png')
    } catch (_) {
      return null
    }
  }

  const handleExportLabels = async () => {
    const selectedAddresses = exportEntries.filter(e => e.selected)

    if (selectedAddresses.length === 0) {
      alert("No addresses selected for export.")
      return
    }

    const logoDataUrl = await loadLogoDataUrl()

    // Start with a 4x6 landscape page for the postcard front — a single
    // template pair (front + back) is generated regardless of how many
    // addresses were selected, since the board bulk-prints one postcard
    // per household and adds the mailing label per-recipient.
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'in',
      format: [6, 4],
    })

    drawPostcardFront(doc, logoDataUrl)
    doc.addPage([6, 4], 'landscape')
    drawPostcardBack(doc)

    // Avery 5160 labels sheet — added AFTER the postcards.
    doc.addPage('letter', 'portrait')
    const labelsPerRow = 3
    const labelsPerCol = 10
    const labelWidth = 2.625
    const labelHeight = 1
    const marginLeft = 0.1875
    const marginTop = 0.5
    const gutter = 0.125

    selectedAddresses.forEach((addr, index) => {
      const pageIndex = Math.floor(index / (labelsPerRow * labelsPerCol))
      const posOnPage = index % (labelsPerRow * labelsPerCol)
      const col = posOnPage % labelsPerRow
      const row = Math.floor(posOnPage / labelsPerRow)

      if (index > 0 && posOnPage === 0) doc.addPage('letter', 'portrait')

      const x = marginLeft + col * (labelWidth + gutter) + 0.1
      const y = marginTop + row * labelHeight + 0.2

      doc.setFontSize(9)
      doc.setFont("helvetica", "bold")
      doc.text(neighborhoodName || "Your Neighborhood", x, y)

      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      if (addr.invitationCode) {
        doc.text("Invitation Code: " + addr.invitationCode, x, y + 0.15)
      }

      // Split addr.address into "street" + "city, state zip" so the label
      // formats like a real mailing label and we don't double-print the
      // city/state/zip (which is already inside addr.address).
      const { streetLine, cityStateZipLine } = splitMailingAddress(addr)
      doc.text(streetLine, x, y + 0.3)
      doc.text(cityStateZipLine, x, y + 0.45)
    })

    doc.save(`NeighborTopia-Invitations-for-${(neighborhoodName || "Neighborhood").replace(/\s+/g, "-")}.pdf`)
    setShowExportModal(false)
    setExportOptions({ active: false, pending: false })
  }

  // Open Email Invitations modal with pending households
  const openEmailModal = () => {
    const pendingAddresses = addresses.filter(a => a.status === 'pending' && a.invitationCode)
    const entries = pendingAddresses.map(addr => ({
      address: addr.address,
      addressDocId: addr.id,
      registrationCode: addr.invitationCode,
      neighborhoodId: addr.neighborhoodId,
      email: '',
      selected: false,
      // Track previous email status
      lastEmailSentTo: addr.invitationEmailSentTo || null,
      lastEmailSentAt: addr.invitationEmailSentAt || null,
    }))
    setEmailEntries(entries)
    setEmailResults(null)
    setEmailSearch('')
    setShowEmailModal(true)
  }

  // Open the Email Invitations modal pre-populated for a single pending
  // address. If the address has previously been emailed (and the recipient
  // never activated the invitation), the same email is pre-filled and the
  // row is pre-selected so a quick "Send" instantly resends. Used by the
  // green Mail button on each Pending row.
  const openEmailModalForOne = (addr) => {
    if (!addr || !addr.invitationCode) return
    const previousEmail = addr.invitationEmailSentTo || ''
    const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(previousEmail.trim())
    setEmailEntries([{
      address: addr.address,
      addressDocId: addr.id,
      registrationCode: addr.invitationCode,
      neighborhoodId: addr.neighborhoodId,
      email: previousEmail,
      selected: looksValid,
      lastEmailSentTo: addr.invitationEmailSentTo || null,
      lastEmailSentAt: addr.invitationEmailSentAt || null,
    }])
    setEmailResults(null)
    setEmailSearch('')
    setShowEmailModal(true)
  }

  // Send invitation emails
  const handleSendInvitationEmails = async () => {
    const toSend = emailEntries.filter(e => e.selected && isValidEmail(e.email))
    if (toSend.length === 0) return

    setSendingEmails(true)
    setEmailResults(null)

    try {
      const { httpsCallable } = await import('firebase/functions')
      const { functions } = await import('../utils/firebase')
      const sendInvitationEmails = httpsCallable(functions, 'sendInvitationEmails')

      const result = await sendInvitationEmails({
        invitations: toSend.map(e => ({
          email: e.email.trim(),
          address: e.address,
          registrationCode: e.registrationCode,
          addressDocId: e.addressDocId,
          neighborhoodId: e.neighborhoodId,
        })),
        neighborhoodName: neighborhoodName,
      })

      setEmailResults(result.data)

      // Refresh addresses to pick up the new email tracking fields
      if (result.data.successCount > 0) {
        setTimeout(() => loadAddresses(), 1500)
      }
    } catch (error) {
      console.error('Error sending invitation emails:', error)
      setEmailResults({ success: false, error: error.message })
    } finally {
      setSendingEmails(false)
    }
  }

  // Update email entry
  const updateEmailEntry = (index, field, value) => {
    setEmailEntries(prev => prev.map((e, i) =>
      i === index ? { ...e, [field]: value } : e
    ))
  }

  // Validate email format
  const isValidEmail = (email) => {
    const trimmed = email.trim()
    if (!trimmed) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
  }

  // Toggle all email entries
  const toggleAllEmails = (checked) => {
    setEmailEntries(prev => prev.map(e => ({
      ...e,
      selected: isValidEmail(e.email) ? checked : false
    })))
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading addresses...</p>
      </div>
    )
  }

  return (
    <div className="manage-neighbors-section">
      <div className="section-header">
        <div className="header-title-row">
          <h2>Manage Households</h2>
          <button className="btn-add-circle" data-walk="households-add-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={24} />
          </button>
        </div>
        {statusFilter !== 'active' && (
          <div className="header-buttons-row">
            <button className="btn-export" data-walk="export-labels-btn" onClick={() => {
              const pendingAddresses = addresses.filter(a => a.status === 'pending' && a.invitationCode)
              setExportEntries(pendingAddresses.map(addr => ({ ...addr, selected: true })))
              setShowExportModal(true)
            }}>
              <Download size={18} /> Export Invitations for Mailing
            </button>
            <button className="btn-export btn-email-invite" data-walk="email-invitations-btn" onClick={openEmailModal}>
              <Mail size={18} /> Email Invitations
            </button>
          </div>
        )}
      </div>

      <div className="filters-row">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search addresses or neighbors..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="clear-btn"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              title="Clear search"
            >
              <X size={20} />
            </button>
          )}
        </div>
        
        <div className="status-filters">
          <button
            className={`filter-chip ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            Active ({stats.active})
          </button>
          <button
            className={`filter-chip ${statusFilter === 'inactive' ? 'active' : ''}`}
            onClick={() => setStatusFilter('inactive')}
            data-walk="households-pending-filter"
          >
            Inactive ({stats.inactive})
          </button>
        </div>
      </div>

      <p className="status-description">
        {statusFilter === 'active' && 'These are your neighborhood households that have successfully registered!'}
        {statusFilter === 'inactive' && 'These are your neighborhood households that are either vacant or have pending invitations - make sure you share the registration codes with the pending households!'}
      </p>

      {filteredAddresses.length === 0 ? (
        <div className="empty-state">
          <MapPin size={48} />
          <h3>{addresses.length === 0 ? 'No addresses yet' : 'No matching addresses'}</h3>
          <p>
            {addresses.length === 0 
              ? 'Add your neighborhood addresses to get started.'
              : 'Try adjusting your search or filters.'}
          </p>
          {addresses.length === 0 && (
            <button className="btn-add-circle" onClick={() => setShowAddModal(true)}>
              <Plus size={24} />
            </button>
          )}
        </div>
      ) : (
        <div className={`addresses-table ${statusFilter === 'active' ? 'no-code-col' : ''}`}>
          <div className="table-header">
            <div className="col-address">Address</div>
            <div className="col-status">Status</div>
            {statusFilter === 'inactive' && <div className="col-code">Registration Code</div>}
            {statusFilter === 'active' && <div className="col-resident">Neighbor</div>}
            <div className="col-actions">Actions</div>
          </div>
          
          {filteredAddresses.map((addr, _rowIdx) => (
            <div key={addr.id} className={`table-row status-${addr.status}`}>
              <div className="col-address">
                <MapPin size={16} />
                <div className="address-lines">
                  <div>{formatAddressDisplay(addr).line1}</div>
                  {formatAddressDisplay(addr).line2 && <div>{formatAddressDisplay(addr).line2}</div>}
                  {formatAddressDisplay(addr).line3 && <div className="city-state">{formatAddressDisplay(addr).line3}</div>}
                </div>
              </div>
              
              <div className="col-status">
                {getStatusBadge(addr.status)}
                {addr.invitationEmailSentTo && addr.status === 'pending' && (
                  <div
                    className="email-sent-indicator"
                    title={`Emailed to ${addr.invitationEmailSentTo}${
                      formatEmailSentAt(addr.invitationEmailSentAt)
                        ? ' on ' + formatEmailSentAt(addr.invitationEmailSentAt, { long: true })
                        : ''
                    }`}
                  >
                    <Mail size={12} /> Emailed
                    {formatEmailSentAt(addr.invitationEmailSentAt) && (
                      <span className="email-sent-date">
                        {' '}{formatEmailSentAt(addr.invitationEmailSentAt)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {statusFilter === 'inactive' && (
                <div className="col-code">
                  {addr.invitationCode ? (
                    <div className="code-display">
                      <code>{addr.invitationCode}</code>
                      <button
                        className="icon-btn"
                        onClick={() => copyToClipboard(addr.invitationCode)}
                        title="Copy code"
                      >
                        {copiedCode === addr.invitationCode ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  ) : (
                    <span className="no-code">—</span>
                  )}
                </div>
              )}
              
              {statusFilter === 'active' && (
                <div className="col-resident">
                  {addr.residentName ? (
                    <div className="resident-info">
                      <div className="resident-info-header">
                        <ResidentAvatar
                          name={addr.residentName}
                          photoURL={addr.residentPhotoURL}
                        />
                        <span>{addr.residentName}</span>
                      </div>
                      {(addr.isAdmin || addr.isBoardMember) && (
                        <div className="resident-badges">
                          {addr.isAdmin && (
                            <span className="resident-badge admin">
                              {addr.adminTitle ? `Neighborhood Admin · ${addr.adminTitle}` : 'Neighborhood Admin'}
                            </span>
                          )}
                          {addr.isBoardMember && (
                            <span className="resident-badge board-member">
                              {addr.boardTitle ? `Board Member · ${addr.boardTitle}` : 'Board Member'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="no-resident">—</span>
                  )}
                </div>
              )}
              
              <div className="col-actions">
                {statusFilter === 'active' && (
                  <>
                    <button
                      className="btn-small btn-outline"
                      onClick={() => handleViewHousehold(addr)}
                      title="View Household Members"
                    >
                      <Users size={14} />
                    </button>

                    {addr.address !== userProfile.address && (
                      <button
                        className="btn-small btn-warning-outline"
                        onClick={() => handleVacateAddress(addr.id, addr.address)}
                        title="Vacate Household - best to use when the neighbor has vacated the household"
                      >
                        <MinusCircle size={14} />
                      </button>
                    )}

                    <div className="dropdown-container">
                      <button
                        className="btn-small btn-outline"
                        onClick={(e) => {
                          handleDropdownClick(addr.id, e.currentTarget)
                          setOpenMenuId(openMenuId === addr.id ? null : addr.id)
                        }}
                        title="More options"
                      >
                        <MoreVertical size={14} />
                      </button>
                      {openMenuId === addr.id && dropdownAnchor?.addrId === addr.id && createPortal(
                        <>
                          {/* No full-screen backdrop — outside-click is
                              detected by a document-level listener (see the
                              useEffect that watches openMenuId). The backdrop
                              approach kept capturing clicks meant for the
                              menu despite z-index ordering. */}
                          <div
                            className="dropdown-menu dropdown-menu-floating"
                            ref={(el) => { dropdownRefs.current[addr.id] = el }}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                              position: 'fixed',
                              top: dropdownAnchor.openUp ? 'auto' : dropdownAnchor.top + 4,
                              bottom: dropdownAnchor.openUp
                                ? window.innerHeight - dropdownAnchor.top + 4
                                : 'auto',
                              left: dropdownAnchor.left,
                              transform: 'translateX(-100%)', // right-align to button's right edge
                            }}
                          >
                            {addr.isAdmin ? (
                              <>
                                <button onClick={() => { handleSetRole(addr, 'editAdminTitle'); setOpenMenuId(null); }}>
                                  <Shield size={14} /> {addr.adminTitle ? 'Edit admin title' : 'Set admin title'}
                                </button>
                                {!isSelfRow(addr) && (
                                  <button onClick={() => { handleSetRole(addr, 'removeAdmin'); setOpenMenuId(null); }}>
                                    <Shield size={14} /> Remove as Neighborhood Admin
                                  </button>
                                )}
                                <button onClick={() => { handleSetRole(addr, 'switchToBoardMember'); setOpenMenuId(null); }}>
                                  <User size={14} /> Switch to Board Member
                                </button>
                              </>
                            ) : addr.isBoardMember ? (
                              <>
                                <button onClick={() => { handleSetRole(addr, 'editBoardTitle'); setOpenMenuId(null); }}>
                                  <User size={14} /> {addr.boardTitle ? 'Edit board title' : 'Set board title'}
                                </button>
                                {!isSelfRow(addr) && (
                                  <button onClick={() => { handleSetRole(addr, 'removeBoardMember'); setOpenMenuId(null); }}>
                                    <User size={14} /> Remove as Board Member
                                  </button>
                                )}
                                <button onClick={() => { handleSetRole(addr, 'switchToAdmin'); setOpenMenuId(null); }}>
                                  <Shield size={14} /> Switch to Neighborhood Admin
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => { handleSetRole(addr, 'makeAdmin'); setOpenMenuId(null); }}>
                                  <Shield size={14} /> Make Neighborhood Admin
                                </button>
                                <button onClick={() => { handleSetRole(addr, 'makeBoardMember'); setOpenMenuId(null); }}>
                                  <User size={14} /> Make Board Member
                                </button>
                              </>
                            )}
                          </div>
                        </>,
                        document.body
                      )}
                    </div>
                  </>
                )}

                {statusFilter === 'inactive' && addr.status === 'pending' && (
                  <>
                    <button
                      className="btn-small btn-email-pending"
                      onClick={() => openEmailModalForOne(addr)}
                      title={addr.invitationEmailSentTo
                        ? `Resend invitation email to ${addr.invitationEmailSentTo}`
                        : 'Email this household their invitation code'}
                    >
                      <Mail size={14} />
                    </button>
                    <button
                      className="btn-small btn-warning-outline"
                      onClick={() => handleVacateAddress(addr.id, addr.address)}
                      title="Cancel Registration Code"
                    >
                      <MinusCircle size={14} />
                    </button>
                    <button
                      className="btn-small btn-danger-outline"
                      onClick={() => handleDeleteAddress(addr.id, addr.address)}
                      title="Delete Household"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}

                {statusFilter === 'inactive' && addr.status === 'vacant' && (
                  <>
                    <button
                      className="btn-icon btn-generate-code"
                      data-walk={_rowIdx === 0 ? 'generate-code-btn' : undefined}
                      onClick={() => handleGenerateCode(addr.id, addr.address)}
                      disabled={generatingCode === addr.id}
                      title="Generate Registration Code - best to use when a new neighbor has moved into the household"
                    >
                      {generatingCode === addr.id ? <RefreshCw size={18} className="spinning" /> : <PlusCircle size={18} />}
                    </button>
                    <button
                      className="btn-small btn-danger-outline"
                      onClick={() => handleDeleteAddress(addr.id, addr.address)}
                      title="Delete Household"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Households</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="mode-tabs">
                <button 
                  className={`mode-tab ${addMode === 'single' ? 'active' : ''}`}
                  onClick={() => setAddMode('single')}
                >
                  Single Household
                </button>
                <button 
                  className={`mode-tab ${addMode === 'bulk' ? 'active' : ''}`}
                  onClick={() => setAddMode('bulk')}
                >
                  Bulk Households
                </button>
              </div>
              
              {addError && (
                <div className="error-message">{addError}</div>
              )}
              
              {addMode === 'single' ? (
                <div className="address-form">
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label>Address Line 1 *</label>
                      <input
                        type="text"
                        placeholder="123 Main Street"
                        value={singleAddress.address1}
                        onChange={e => setSingleAddress(prev => ({ ...prev, address1: e.target.value }))}
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label>Address Line 2</label>
                      <input
                        type="text"
                        placeholder="Apt, Suite, etc."
                        value={singleAddress.address2}
                        onChange={e => setSingleAddress(prev => ({ ...prev, address2: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label>City *</label>
                      <input
                        type="text"
                        placeholder="City"
                        value={singleAddress.city}
                        onChange={e => setSingleAddress(prev => ({ ...prev, city: e.target.value }))}
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label>State *</label>
                      <select
                        value={singleAddress.state}
                        onChange={e => setSingleAddress(prev => ({ ...prev, state: e.target.value }))}
                      >
                        <option value="">Select</option>
                        {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </div>
                    <div className="form-group flex-1">
                      <label>ZIP *</label>
                      <input
                        type="text"
                        placeholder="12345"
                        maxLength={5}
                        value={singleAddress.zip}
                        onChange={e => setSingleAddress(prev => ({ ...prev, zip: e.target.value.replace(/\D/g, '')}))}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bulk-form">
                  {/*
                    CSV toolbar. Two actions side by side:
                      - Download Template — creates a starter CSV with
                        the expected column names so admins editing in
                        Excel don't have to guess.
                      - Upload CSV — triggers a hidden file input; the
                        contents replace the visible rows below and
                        surface a note about any rows that need review.
                    Left-aligned so it reads as a helper strip rather
                    than competing with the primary form actions in the
                    modal footer.
                  */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid var(--color-border)',
                  }}>
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={downloadCsvTemplate}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13 }}
                    >
                      <Download size={14} />
                      <span>Download Template</span>
                    </button>
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => csvFileInputRef.current?.click()}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13 }}
                    >
                      <Upload size={14} />
                      <span>Upload CSV</span>
                    </button>
                    <input
                      ref={csvFileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleCsvFileSelected}
                      style={{ display: 'none' }}
                    />
                    {csvImportNote && (
                      <span style={{
                        fontSize: 12.5,
                        color: 'var(--color-text-secondary)',
                        marginLeft: 4,
                        lineHeight: 1.35,
                      }}>{csvImportNote}</span>
                    )}
                  </div>

                  <div className="apply-to-all">
                    <span className="apply-label">Apply to all rows:</span>
                    <input
                      type="text"
                      placeholder="City"
                      value={applyToAll.city}
                      onChange={e => handleApplyToAll('city', e.target.value)}
                      className="apply-input"
                    />
                    <select
                      value={applyToAll.state}
                      onChange={e => handleApplyToAll('state', e.target.value)}
                      className="apply-select"
                    >
                      <option value="">State</option>
                      {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <input
                      type="text"
                      placeholder="ZIP"
                      maxLength={5}
                      value={applyToAll.zip}
                      onChange={e => handleApplyToAll('zip', e.target.value.replace(/\D/g, ''))}
                      className="apply-input apply-zip"
                    />
                  </div>
                  
                  <div className="bulk-table">
                    <div className="bulk-header">
                      <div className="bulk-col addr1">Address 1 *</div>
                      <div className="bulk-col addr2">Address 2</div>
                      <div className="bulk-col city">City *</div>
                      <div className="bulk-col state">State *</div>
                      <div className="bulk-col zip">ZIP *</div>
                      <div className="bulk-col actions"></div>
                    </div>
                    {bulkRows.map((row, index) => (
                      <div key={index} className="bulk-row">
                        <input
                          className="bulk-col addr1"
                          type="text"
                          placeholder="123 Main St"
                          value={row.address1}
                          onChange={e => updateBulkRow(index, 'address1', e.target.value)}
                        />
                        <input
                          className="bulk-col addr2"
                          type="text"
                          placeholder="Apt 1"
                          value={row.address2}
                          onChange={e => updateBulkRow(index, 'address2', e.target.value)}
                        />
                        <input
                          className="bulk-col city"
                          type="text"
                          placeholder="City"
                          value={row.city}
                          onChange={e => updateBulkRow(index, 'city', e.target.value)}
                        />
                        <select
                          className="bulk-col state"
                          value={row.state}
                          onChange={e => updateBulkRow(index, 'state', e.target.value)}
                        >
                          <option value="">--</option>
                          {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                        </select>
                        <input
                          className="bulk-col zip"
                          type="text"
                          placeholder="12345"
                          maxLength={5}
                          value={row.zip}
                          onChange={e => updateBulkRow(index, 'zip', e.target.value.replace(/\D/g, ''))}
                        />
                        <button
                          className="bulk-col actions btn-icon"
                          onClick={() => removeBulkRow(index)}
                          disabled={bulkRows.length <= 1}
                          type="button"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn-text" onClick={addBulkRow} type="button">
                    <Plus size={16} /> Add Row
                  </button>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddAddresses}
                disabled={adding}
              >
                {adding ? 'Adding...' : addMode === 'single' ? 'Add Household' : 'Add Households'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Household Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Download size={20} /> Export Mailing Labels</h3>
              <button className="close-btn" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom: '0.75rem', color: 'var(--color-text-secondary)'}}>
                Select the pending households you'd like to export mailing labels for. A PDF with Avery 5160 compatible labels will be generated.
              </p>
              <div className="email-list-header">
                <label className="email-select-all">
                  <input
                    type="checkbox"
                    onChange={e => setExportEntries(prev => prev.map(entry => ({ ...entry, selected: e.target.checked })))}
                    checked={exportEntries.length > 0 && exportEntries.every(e => e.selected)}
                  />
                  Select All
                </label>
                <span className="email-count">
                  {exportEntries.filter(e => e.selected).length} of {exportEntries.length} selected
                </span>
              </div>
              <div className="email-entries-list">
                {exportEntries.map((entry, index) => (
                  <div key={index} className={`email-entry ${entry.selected ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={entry.selected}
                      onChange={e => setExportEntries(prev => prev.map((item, i) => i === index ? { ...item, selected: e.target.checked } : item))}
                    />
                    <div className="email-entry-info">
                      <div className="email-entry-address">
                        <MapPin size={14} /> {entry.address}
                      </div>
                      <div className="email-entry-code">Code: <strong>{entry.invitationCode}</strong></div>
                    </div>
                  </div>
                ))}
                {exportEntries.length === 0 && (
                  <p style={{textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem'}}>No pending households to export.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-outline" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleExportLabels}
                disabled={exportEntries.filter(e => e.selected).length === 0}
              >
                <Download size={16} /> Export {exportEntries.filter(e => e.selected).length} Label{exportEntries.filter(e => e.selected).length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Invitations Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={() => !sendingEmails && setShowEmailModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Mail size={20} /> Email Invitations</h3>
              <button className="close-btn" onClick={() => !sendingEmails && setShowEmailModal(false)} disabled={sendingEmails}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {emailResults ? (
                <div className="email-results">
                  {emailResults.error ? (
                    <div className="email-result-error">
                      <AlertCircle size={20} />
                      <p>Failed to send emails: {emailResults.error}</p>
                    </div>
                  ) : (
                    <>
                      <div className="email-result-summary">
                        <CheckCircle size={20} color="#2e7d32" />
                        <p><strong>{emailResults.successCount}</strong> email{emailResults.successCount !== 1 ? 's' : ''} sent successfully{emailResults.failCount > 0 && <>, <strong style={{color: '#d32f2f'}}>{emailResults.failCount} failed</strong></>}</p>
                      </div>
                      <div className="email-result-list">
                        {emailResults.results?.map((r, i) => (
                          <div key={i} className={`email-result-item ${r.success ? 'success' : 'failed'}`}>
                            <span>{r.success ? <CheckCircle size={14} /> : <AlertCircle size={14} />}</span>
                            <span className="email-result-address">{r.address}</span>
                            <span className="email-result-email">{r.email}</span>
                            <span className="email-result-status">{r.success ? 'Sent' : r.error}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <p style={{marginBottom: '0.75rem', color: 'var(--color-text-secondary)'}}>
                    This is a great tool to invite your neighbors if you know their email addresses.
                  </p>

                  {/* Free-text search across address + registration code so an admin
                      with 100+ pending households can find a specific row fast. */}
                  {emailEntries.length > 5 && (
                    <div className="email-search-box">
                      <Search size={16} />
                      <input
                        type="text"
                        placeholder="Search by address or code..."
                        value={emailSearch}
                        onChange={e => setEmailSearch(e.target.value)}
                      />
                      {emailSearch && (
                        <button
                          className="email-search-clear"
                          onClick={() => setEmailSearch('')}
                          aria-label="Clear search"
                          title="Clear search"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  )}

                  <div className="email-list-header">
                    <span className="email-count">
                      {emailEntries.filter(e => e.selected && isValidEmail(e.email)).length} of {emailEntries.length} selected
                      {emailSearch.trim() && (() => {
                        const needle = emailSearch.trim().toLowerCase()
                        const shown = emailEntries.filter(e =>
                          (e.address || '').toLowerCase().includes(needle) ||
                          (e.registrationCode || '').toLowerCase().includes(needle)
                        ).length
                        return ` · ${shown} match${shown === 1 ? '' : 'es'}`
                      })()}
                    </span>
                  </div>
                  <div className="email-entries-list">
                    {/* IMPORTANT: keep the original index when filtering so
                        updateEmailEntry(index, ...) still targets the right
                        row in the full emailEntries array. */}
                    {emailEntries.map((entry, index) => {
                      const needle = emailSearch.trim().toLowerCase()
                      if (needle) {
                        const haystack = `${entry.address || ''} ${entry.registrationCode || ''}`.toLowerCase()
                        if (!haystack.includes(needle)) return null
                      }
                      return (
                      <div key={index} className={`email-entry ${entry.selected ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={entry.selected}
                          onChange={e => updateEmailEntry(index, 'selected', e.target.checked)}
                          disabled={!isValidEmail(entry.email)}
                        />
                        <div className="email-entry-info">
                          <div className="email-entry-address">
                            <MapPin size={14} /> {entry.address}
                          </div>
                          <div className="email-entry-code">Code: <strong>{entry.registrationCode}</strong></div>
                          {entry.lastEmailSentTo && (
                            <div className="email-entry-sent">
                              <CheckCircle size={12} /> Last sent to {entry.lastEmailSentTo}
                              {entry.lastEmailSentAt && ` on ${new Date(entry.lastEmailSentAt.seconds ? entry.lastEmailSentAt.seconds * 1000 : entry.lastEmailSentAt).toLocaleDateString()}`}
                            </div>
                          )}
                        </div>
                        <input
                          type="email"
                          className="email-entry-input"
                          placeholder="Enter email address"
                          value={entry.email}
                          onChange={e => {
                            updateEmailEntry(index, 'email', e.target.value)
                            if (isValidEmail(e.target.value) && !entry.selected) {
                              updateEmailEntry(index, 'selected', true)
                            }
                            if (!isValidEmail(e.target.value) && entry.selected) {
                              updateEmailEntry(index, 'selected', false)
                            }
                          }}
                        />
                        {entry.email.trim() && !isValidEmail(entry.email) && (
                          <div style={{color: '#d32f2f', fontSize: '0.75rem', marginTop: '2px'}}>
                            <AlertCircle size={12} style={{verticalAlign: 'middle', marginRight: '3px'}} />
                            Invalid email address
                          </div>
                        )}
                      </div>
                      )
                    })}
                    {emailEntries.length === 0 && (
                      <p style={{textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem'}}>No pending households to email.</p>
                    )}
                    {emailEntries.length > 0 && emailSearch.trim() && emailEntries.every(e => {
                      const needle = emailSearch.trim().toLowerCase()
                      const haystack = `${e.address || ''} ${e.registrationCode || ''}`.toLowerCase()
                      return !haystack.includes(needle)
                    }) && (
                      <p style={{textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem'}}>
                        No households match "{emailSearch}".
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {emailResults ? (
                <button className="btn-primary" onClick={() => setShowEmailModal(false)}>
                  Done
                </button>
              ) : (
                <>
                  <button className="btn-outline" onClick={() => setShowEmailModal(false)} disabled={sendingEmails}>
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSendInvitationEmails}
                    disabled={sendingEmails || emailEntries.filter(e => e.selected && isValidEmail(e.email)).length === 0}
                  >
                    {sendingEmails ? (
                      <><RefreshCw size={16} className="spinning" /> Sending...</>
                    ) : (
                      <><Send size={16} /> Send {emailEntries.filter(e => e.selected && isValidEmail(e.email)).length} Email{emailEntries.filter(e => e.selected && isValidEmail(e.email)).length !== 1 ? 's' : ''}</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showHouseholdModal && (
        <div className="modal-overlay" onClick={() => setShowHouseholdModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Household Members</h3>
              <button className="close-btn" onClick={() => setShowHouseholdModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="household-address"><MapPin size={16} /> {selectedAddress?.address}</p>
              {householdMembers.length === 0 ? (
                <p className="no-members">No members found for this address.</p>
              ) : (
                <div className="household-list">
                  {householdMembers.map(member => (
                    <div key={member.id} className="household-member">
                      <div className="member-info">
                        <div className="member-avatar">
                          {member.memberType === 'pending' ? '?' : (member.name?.charAt(0).toUpperCase() || "U")}
                        </div>
                        <div className="member-details">
                          <span className="member-name">
                            {member.memberType === 'pending' ? (member.inviteeName || 'Pending Invite') : member.name}
                          </span>
                          <span className="member-email">{member.email || member.inviteeEmail || ''}</span>
                          <div className="member-badges">
                            {member.memberType === 'pending' && <span className="badge pending-badge">Pending</span>}
                            {member.isAdmin && (
                              <span className="badge admin">
                                {member.adminTitle ? `Neighborhood Admin · ${member.adminTitle}` : 'Neighborhood Admin'}
                              </span>
                            )}
                            {member.isBoardMember && (
                              <span className="badge board">
                                {member.boardTitle ? `Board Member · ${member.boardTitle}` : 'Board Member'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowHouseholdModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManageNeighborsSection
